import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { gameLogo, stadium } from '../components/caseStudies/worldCup/assets';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const CaseStudiesPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  // Our own results only; the client's audience figures stay private.
  const stats = t('caseStudies.worldCup.card.stats', { returnObjects: true });

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
      }}
    >
      <Container maxWidth="lg">
        {/* Page header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <MotionTypography
            component="h1"
            variant="h3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              fontSize: { xs: '1.9rem', md: '2.4rem' },
              fontWeight: 700,
              color: 'var(--text-primary)',
              mb: 2,
            }}
          >
            {t('caseStudies.title')}
          </MotionTypography>
          <Box
            sx={{
              height: '3px',
              width: '60px',
              background: 'var(--text-primary)',
              borderRadius: 'var(--radius-full)',
              mx: 'auto',
              mb: 3,
            }}
          />
          <Typography
            variant="body1"
            sx={{ color: 'var(--text-secondary)', fontSize: '1.2rem', maxWidth: '640px', mx: 'auto' }}
          >
            {t('caseStudies.subtitle')}
          </Typography>
        </Box>

        {/* World Cup 2026: the whole card links to the case study */}
        <MotionBox
          component={RouterLink}
          to="/case-studies/world-cup-2026"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' },
            overflow: 'hidden',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-secondary)',
            boxShadow: 'var(--shadow-md)',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'box-shadow var(--transition-slow), transform var(--transition-slow)',
            '&:hover': { boxShadow: 'var(--shadow-xl)', transform: 'translateY(-4px)' },
            '&:hover .cs-card-cta': { gap: '14px' },
            '&:focus-visible': { outline: '3px solid var(--text-primary)', outlineOffset: '3px' },
          }}
        >
          {/* Visual: the game's stadium and crest */}
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 220, md: 360 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: `linear-gradient(180deg, rgba(16, 14, 9, 0.15) 0%, rgba(16, 14, 9, 0.75) 100%), url(${stadium})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <Box
              component="img"
              src={gameLogo}
              alt={t('caseStudies.worldCup.card.title')}
              sx={{ width: { xs: 120, md: 170 }, height: 'auto', filter: 'drop-shadow(0 10px 24px rgba(0, 0, 0, 0.6))' }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <Typography
              sx={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.12em' }}
            >
              {t('caseStudies.worldCup.card.eyebrow')}
            </Typography>
            <Typography
              component="h2"
              sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, lineHeight: 1.15 }}
            >
              {t('caseStudies.worldCup.card.title')}
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '1.15rem', lineHeight: 1.7 }}>
              {t('caseStudies.worldCup.card.summary')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2,
                py: 2,
                my: 1,
                borderTop: '1px solid var(--border-color)',
                borderBottom: '1px solid var(--border-color)',
              }}
            >
              {stats.map((stat) => (
                <Box key={stat.fig} sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: 'var(--text-primary)',
                      fontWeight: 800,
                      fontSize: { xs: '1.35rem', md: '1.6rem' },
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.2,
                    }}
                  >
                    <bdi>{stat.fig}</bdi>
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.4 }}>
                    {stat.lbl}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              className="cs-card-cta"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--text-primary)',
                fontWeight: 700,
                fontSize: '1.05rem',
                transition: 'gap var(--transition-base)',
              }}
            >
              {t('caseStudies.readMore')}
              <ArrowForwardIcon fontSize="small" sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
            </Box>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default CaseStudiesPage;
